import type { FoodFeedback, FoodFeedbackResult } from "@/shared/contracts";

export type FoodFeedbackScope = { mode: "live" | "fixture"; userId: string; householdId: string; petId: string; recommendationId: string };
type Receipt = { schema: 1; scope: FoodFeedbackScope; idempotencyKey: string; state: "pending" | "unknown" | "blocked" | "confirmed" };
type Read = { kind: "empty" } | { kind: "held"; receipt: Receipt } | { kind: "error" };
const object = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === "object" && !Array.isArray(v);
const exact = (v: Record<string, unknown>, names: string[]) => Object.keys(v).length === names.length && names.every(n => Object.hasOwn(v, n));
const id = (v: unknown): v is string => typeof v === "string" && Boolean(v.trim()) && v.length <= 256 && !/[\u0000-\u001f\u007f]/.test(v);
export const feedbackScopeKey = (s: FoodFeedbackScope) => JSON.stringify([s.mode, s.userId, s.householdId, s.petId, s.recommendationId]);
function validScope(v: unknown): v is FoodFeedbackScope {
  return object(v) && exact(v, ["mode", "userId", "householdId", "petId", "recommendationId"]) && ["live", "fixture"].includes(v.mode as string)
    && id(v.userId) && id(v.householdId) && id(v.petId) && id(v.recommendationId);
}
function valid(v: unknown): v is Receipt {
  return object(v) && exact(v, ["schema", "scope", "idempotencyKey", "state"]) && v.schema === 1 && validScope(v.scope)
    && typeof v.idempotencyKey === "string" && /^[A-Za-z0-9:._-]{1,128}$/.test(v.idempotencyKey) && ["pending", "unknown", "blocked", "confirmed"].includes(v.state as string);
}
const storageKey = (s: FoodFeedbackScope) => "petsoul:food-feedback:v1:" + feedbackScopeKey(s);
export function readFoodFeedbackReceipt(scope: FoodFeedbackScope): Read {
  try {
    if (!validScope(scope)) return { kind: "error" };
    const raw = window.sessionStorage.getItem(storageKey(scope));
    if (raw === null) return { kind: "empty" };
    if (raw.length > 4096) return { kind: "error" };
    const value: unknown = JSON.parse(raw);
    return valid(value) && feedbackScopeKey(value.scope) === feedbackScopeKey(scope) ? { kind: "held", receipt: value } : { kind: "error" };
  } catch { return { kind: "error" }; }
}
function persist(receipt: Receipt): boolean {
  try {
    if (!valid(receipt)) return false;
    const raw = JSON.stringify(receipt), key = storageKey(receipt.scope);
    window.sessionStorage.setItem(key, raw);
    return window.sessionStorage.getItem(key) === raw;
  } catch { return false; }
}
/** No note, reasons, body, preference, token, cookie, response or TTL is persisted. */
export function reserveFoodFeedbackReceipt(scope: FoodFeedbackScope, idempotencyKey: string): "reserved" | "held" | "error" {
  const before = readFoodFeedbackReceipt(scope);
  if (before.kind !== "empty") return before.kind === "error" ? "error" : "held";
  return persist({ schema: 1, scope, idempotencyKey, state: "pending" }) ? "reserved" : "error";
}
export function updateFoodFeedbackReceipt(scope: FoodFeedbackScope, key: string, state: "unknown" | "blocked" | "confirmed"): boolean {
  const before = readFoodFeedbackReceipt(scope);
  if (before.kind !== "held" || before.receipt.idempotencyKey !== key) return false;
  const next = before.receipt.state === "confirmed" || (before.receipt.state === "blocked" && state !== "confirmed") ? before.receipt.state : state;
  return persist({ ...before.receipt, state: next });
}
export function validOriginalFoodFeedback(value: unknown, scope: FoodFeedbackScope): value is FoodFeedback {
  if (!object(value) || !exact(value, ["feedback_id", "recommendation_id", "dish_ids", "verdict", "reasons", "note", "dined_on", "verification"])) return false;
  if (!id(value.feedback_id) || value.recommendation_id !== scope.recommendationId || value.verification !== "self_reported"
      || !["liked", "neutral", "disliked"].includes(value.verdict as string)) return false;
  if (!Array.isArray(value.dish_ids) || !value.dish_ids.every(id) || !Array.isArray(value.reasons) || !value.reasons.every(r => typeof r === "string")
      || (value.note !== null && typeof value.note !== "string") || typeof value.dined_on !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value.dined_on)) return false;
  const date = new Date(value.dined_on);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value.dined_on;
}
export function validFoodFeedbackResult(value: unknown, scope: FoodFeedbackScope): value is FoodFeedbackResult {
  return object(value) && exact(value, ["state", "feedback"]) && (value.state === "completed" ? validOriginalFoodFeedback(value.feedback, scope)
    : (value.state === "unknown" || value.state === "in_progress") && value.feedback === null);
}
